import { useEffect, useState } from 'react';
import { getProfile, saveProfile } from '@/lib/db/profileRepo';
import { AUTOFILL_BOOKMARKLET_HREF } from '@/bookmarklet/generated-link';
import BookmarkletLink from '@/components/BookmarkletLink';
import CopyBookmarkletLinkButton from '@/components/CopyBookmarkletLinkButton';

export default function SettingsPage() {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getProfile().then((profile) => {
      if (profile) {
        setName(profile.name);
        setAddress(profile.address);
        setMobileNumber(profile.mobileNumber);
      }
      setLoaded(true);
    });
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await saveProfile({ name: name.trim(), address: address.trim(), mobileNumber: mobileNumber.trim() });
      setSavedAt(Date.now());
    } catch {
      setError('Please check your name (2+ characters), mobile number (7-15 digits), and address.');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return null;

  return (
    <div className="flex flex-col gap-6 p-4 pt-6">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Saved on this device only — used to pre-fill the IRD form's payer details.
        </p>
      </header>

      <form onSubmit={(e) => void handleSave(e)} className="flex flex-col gap-5 rounded-2xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Personal Information</h2>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="profile-name" className="text-sm font-medium text-slate-700">
            Name
          </label>
          <input
            id="profile-name"
            type="text"
            required
            minLength={2}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="profile-address" className="text-sm font-medium text-slate-700">
            Address
          </label>
          <input
            id="profile-address"
            type="text"
            required
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            autoComplete="street-address"
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="profile-mobile" className="text-sm font-medium text-slate-700">
            Mobile Number
          </label>
          <input
            id="profile-mobile"
            type="text"
            inputMode="tel"
            required
            pattern="[0-9]{7,15}"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            autoComplete="tel"
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900"
          />
        </div>

        {error && (
          <p role="alert" className="text-sm font-medium text-red-700">
            {error}
          </p>
        )}
        {savedAt && !error && <p className="text-sm font-medium text-emerald-700">Saved.</p>}

        <button
          type="submit"
          disabled={saving}
          className="rounded-2xl bg-blue-700 px-6 py-4 text-base font-semibold text-white active:bg-blue-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>

      <section className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Autofill Bookmarklet</h2>
        <p className="text-sm text-slate-600">
          A one-time setup that lets you fill the real IRD form's fields automatically instead of copying each one by
          hand. It never sends anything to any server — on the IRD Assist screen you copy your bill's data, then this
          bookmarklet reads it straight from your clipboard while you're on the real IRD page.
        </p>

        <div role="note" className="rounded-xl bg-blue-50 p-3 text-xs text-blue-900">
          Works reliably on desktop. On a phone, use "Copy Bookmarklet Link" below rather than long-pressing the
          button — if it still doesn't run after that, use the "copy fields manually" cheat sheet on the IRD Assist
          screen instead, which always works everywhere.
        </div>

        <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center">
          <BookmarkletLink
            href={AUTOFILL_BOOKMARKLET_HREF}
            className="inline-block cursor-grab rounded-xl bg-slate-800 px-5 py-3 text-sm font-semibold text-white active:cursor-grabbing"
          >
            📌 IRD Autofill
          </BookmarkletLink>
          <p className="mt-2 text-xs text-slate-500">Desktop: drag this button to your bookmarks bar.</p>
          <div className="mt-3 border-t border-slate-200 pt-3">
            <CopyBookmarkletLinkButton href={AUTOFILL_BOOKMARKLET_HREF} />
            <p className="mt-2 text-xs text-slate-500">Mobile: tap to copy, then paste into a bookmark's URL.</p>
          </div>
        </div>

        <details className="text-sm text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-700">On a phone? (tap for instructions)</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Tap "Copy Bookmarklet Link" above.</li>
            <li>Bookmark this page (or any page), then open your bookmarks and edit that bookmark.</li>
            <li>Paste over the bookmark's URL (replacing it completely) and rename it "IRD Autofill."</li>
            <li>Save. Open the real IRD site in the same browser, then tap the bookmark to run it.</li>
            <li>If it still doesn't do anything, your browser may not support this — try Brave or Firefox instead, or use the cheat sheet.</li>
          </ol>
        </details>

        <p className="text-xs text-slate-400">
          Only fills text fields, the date, and selects "Cash." You still attach the bill photo, solve the security
          check, and submit yourself.
        </p>
      </section>
    </div>
  );
}
